import React from 'react'

import { ActionEmitter, KeybindAction } from '../../keybindings'
import { getLogger } from '@deltachat-desktop/shared/logger'
import { DialogContext } from '../../contexts/DialogContext'
import { I18nContext } from '../../contexts/I18nContext'
import { CompletionMenu } from './completion/CompletionMenu'
import { findActiveToken } from './completion/findActiveToken'
import { emojiProvider } from './completion/emojiProvider'

import type {
  ActiveToken,
  CompletionItem,
  CompletionProvider,
} from './completion/types'

const log = getLogger('renderer/composer/ComposerMessageInput')

const browserSupportsCSSFieldSizing = CSS.supports('field-sizing', 'content')
const maxLines = 9

// Completion providers, tried in order. Emoji now; a future @mention provider
// slots in here with no other change.
const completionProviders: CompletionProvider[] = [emojiProvider]

type CompletionState = {
  open: boolean
  items: CompletionItem[]
  active: number
  token: ActiveToken | null
}
const CLOSED_COMPLETION: CompletionState = {
  open: false,
  items: [],
  active: 0,
  token: null,
}

type ComposerMessageInputProps = {
  text: string
  /**
   * Whether the initial loading of the draft is being performed,
   * e.g. after switching the chat, or after a
   * {@linkcode BackendRemote.rpc.miscSetDraft} from outside of
   * {@linkcode useDraft}.
   */
  loadingDraft: boolean
  /**
   * Whether to render the HTML or to return `null`.
   */
  hidden?: boolean
  isMessageEditingMode: boolean
  chatId: number
  sendMessageOrEditRequest: () => void
  enterKeySends: boolean
  /** Enable the :emoji: completion menu (on by default). */
  completionMenu?: boolean
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void
  // used to show/hide send button
  onChange: (text: string) => void
  /**
   * Called when ArrowUp is pressed and the input is empty.
   * Only relevant for non-editing mode.
   */
  onArrowUpWhenEmpty?: () => void
}

type ComposerMessageInputState = {
  // error?:boolean|Error
  completion: CompletionState
}

export default class ComposerMessageInput extends React.Component<
  ComposerMessageInputProps,
  ComposerMessageInputState
> {
  static contextType = DialogContext
  declare context: React.ContextType<typeof DialogContext>

  composerSize: number
  setCursorPosition: number | false
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
  /** a token dismissed with Escape; stays closed until the term changes */
  completionDismissed: { start: number; term: string } | null
  constructor(props: ComposerMessageInputProps) {
    super(props)

    this.state = { completion: CLOSED_COMPLETION }
    this.composerSize = 48
    this.completionDismissed = null
    this.setComposerSize = this.setComposerSize.bind(this)
    this.setCursorPosition = false
    this.onKeyDown = this.onKeyDown.bind(this)
    this.onChange = this.onChange.bind(this)
    this.insertStringAtCursorPosition =
      this.insertStringAtCursorPosition.bind(this)
    this.onCaretMove = this.onCaretMove.bind(this)
    this.selectCompletion = this.selectCompletion.bind(this)
    this.closeCompletion = this.closeCompletion.bind(this)

    this.textareaRef = React.createRef()
    this.focus = this.focus.bind(this)
  }

  componentDidMount() {
    ActionEmitter.registerHandler(KeybindAction.Composer_Focus, this.focus)
  }

  componentWillUnmount() {
    ActionEmitter.unRegisterHandler(KeybindAction.Composer_Focus, this.focus)
  }

  setComposerSize(size: number) {
    this.composerSize = size
  }

  focus() {
    log.debug('Focus composer message input')
    setTimeout(() => this.textareaRef?.current?.focus())
  }

  componentDidUpdate(prevProps: ComposerMessageInputProps) {
    if (this.setCursorPosition && this.textareaRef.current) {
      this.textareaRef.current.selectionStart = this.setCursorPosition
      this.textareaRef.current.selectionEnd = this.setCursorPosition

      // don't force focus on the message input as long as the emoji picker is open
      if (
        !(
          document.querySelector(':focus')?.tagName?.toLowerCase() ===
          'em-emoji-picker'
        )
      ) {
        // Focus on the current selection, hack for focusing on newlines
        // TODO this pretty much doesn't work,
        // because you can't focus an element that is covered by a dialog.
        // See the comment added in 0c1de2505731b6fc420c8d30cc683a57606d4a4b
        // (https://github.com/deltachat/deltachat-desktop/pull/5127).
        if (this.context.hasOpenDialogs) {
          this.textareaRef.current.blur()
          this.textareaRef.current.focus()
        }
      }

      this.setCursorPosition = false
    } else {
      // This is useful when entering / exiting the message editing mode.
      if (
        prevProps.hidden !== this.props.hidden &&
        !this.props.hidden &&
        this.props.text.length !== 0
      ) {
        this.moveCursorToTheEnd()
      }
    }
    if (
      !browserSupportsCSSFieldSizing &&
      prevProps.chatId === this.props.chatId
    ) {
      this.resizeTextareaAndComposer()
    }
  }

  private moveCursorToTheEnd() {
    if (this.textareaRef.current == null) {
      log.warn(
        'Tried to move the cursor position to the end, ' +
          'but textareaRef.current is',
        this.textareaRef.current
      )
      return
    }
    this.textareaRef.current.selectionStart = this.props.text.length
    this.textareaRef.current.selectionEnd = this.props.text.length
  }

  onChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const text = e.target.value
    this.props.onChange(text)
    this.refreshCompletion(text, e.target.selectionStart ?? text.length)
  }

  /** Re-evaluate the completion token after the text or caret changed. */
  onCaretMove(e: React.SyntheticEvent<HTMLTextAreaElement>) {
    const el = e.currentTarget
    this.refreshCompletion(el.value, el.selectionStart ?? el.value.length)
  }

  refreshCompletion(text: string, caret: number) {
    if (!this.props.completionMenu) {
      this.closeCompletion()
      return
    }
    for (const provider of completionProviders) {
      const token = findActiveToken(text, caret, provider)
      if (!token) {
        continue
      }
      // stay closed on an Escape-dismissed token until its term changes
      const d = this.completionDismissed
      if (d && d.start === token.start && d.term === token.term) {
        this.closeCompletion()
        return
      }
      this.completionDismissed = null
      this.applyCompletion(token, provider.query(token.term))
      return
    }
    this.closeCompletion()
  }

  private applyCompletion(token: ActiveToken, items: CompletionItem[]) {
    this.setState(prev => {
      const p = prev.completion
      // a pure caret move within the same term keeps the highlight;
      // a changed term (typing) resets to the top.
      const sameTerm =
        p.token != null &&
        p.token.start === token.start &&
        p.token.term === token.term
      const active = sameTerm
        ? Math.min(p.active, Math.max(items.length - 1, 0))
        : 0
      return { completion: { open: items.length > 0, items, active, token } }
    })
  }

  closeCompletion() {
    this.setState(prev =>
      prev.completion.open || prev.completion.token
        ? { completion: CLOSED_COMPLETION }
        : null
    )
  }

  private moveCompletionActive(delta: number) {
    this.setState(prev => {
      const n = prev.completion.items.length
      if (n === 0) {
        return null
      }
      const active = (prev.completion.active + delta + n) % n
      return { completion: { ...prev.completion, active } }
    })
  }

  selectCompletion(item: CompletionItem) {
    const { token } = this.state.completion
    if (!token) {
      return
    }
    const text = this.props.text
    const updated = text.slice(0, token.start) + item.value + text.slice(token.end)
    // reuse the existing caret-restore path (componentDidUpdate)
    this.setCursorPosition = token.start + item.value.length
    this.closeCompletion()
    this.props.onChange(updated)
  }

  /** Handle the keys the open menu owns. Returns true if the event was consumed. */
  private onCompletionKeyDown(
    e: React.KeyboardEvent<HTMLTextAreaElement>
  ): boolean {
    const { open, items, active } = this.state.completion
    if (!open) {
      return false
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        e.stopPropagation()
        this.moveCompletionActive(1)
        return true
      case 'ArrowUp':
        e.preventDefault()
        e.stopPropagation()
        this.moveCompletionActive(-1)
        return true
      case 'Enter':
        if (e.shiftKey) {
          return false // Shift+Enter stays a newline
        }
        e.preventDefault()
        e.stopPropagation()
        if (items[active]) {
          this.selectCompletion(items[active])
        }
        return true
      case 'Escape': {
        e.preventDefault()
        e.stopPropagation()
        const token = this.state.completion.token
        if (token) {
          this.completionDismissed = { start: token.start, term: token.term }
        }
        this.closeCompletion()
        return true
      }
      default:
        return false
    }
  }

  keyEventToAction(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    const enterKeySends = this.props.enterKeySends

    // ENTER + CTRL
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      return 'SEND'
      // ENTER
    } else if (e.key === 'Enter' && !e.shiftKey) {
      return enterKeySends ? 'SEND' : undefined
    }
  }

  onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    // While the completion menu is open it owns Arrow/Enter/Escape, so the
    // composer's Enter=send and ArrowUp=edit-last never fire.
    if (this.onCompletionKeyDown(e)) {
      return
    }

    const action = this.keyEventToAction(e)

    if (action === 'SEND') {
      this.props.sendMessageOrEditRequest()
      e.preventDefault()
      e.stopPropagation()
    } else if (
      e.key === 'ArrowUp' &&
      !e.ctrlKey &&
      !e.metaKey &&
      !e.altKey &&
      !e.shiftKey &&
      this.props.text === '' &&
      this.props.onArrowUpWhenEmpty
    ) {
      this.props.onArrowUpWhenEmpty()
      e.preventDefault()
      e.stopPropagation()
    }
  }

  resizeTextareaAndComposer() {
    const maxScrollHeight = maxLines * 24

    const el = this.textareaRef.current

    if (!el) {
      return
    }

    // We need to set the textarea height first to `auto` to get the real needed
    // scrollHeight. Ugly hack.
    el.style.height = 'auto'
    const scrollHeight = el.scrollHeight

    if (scrollHeight + 16 === this.composerSize) {
      el.style.height = scrollHeight + 'px'
      return
    }

    if (scrollHeight > maxScrollHeight && el.classList.contains('scroll')) {
      el.style.height = maxScrollHeight + 'px'
      return
    }

    if (scrollHeight < maxScrollHeight) {
      this.setComposerSize(scrollHeight + 16)
      el.style.height = scrollHeight + 'px'
      el.classList.remove('scroll')
    } else {
      this.setComposerSize(maxScrollHeight + 16)
      el.style.height = maxScrollHeight + 'px'
      el.classList.add('scroll')
    }
  }

  insertStringAtCursorPosition(str: string) {
    const textareaElem = this.textareaRef.current
    if (!textareaElem) {
      throw new Error('textareaElem missing')
    }
    const { selectionStart, selectionEnd } = textareaElem
    const textValue = this.props.text

    const textBeforeCursor = textValue.slice(0, selectionStart)
    const textAfterCursor = textValue.slice(selectionEnd)

    const updatedText = textBeforeCursor + str + textAfterCursor

    this.setCursorPosition = textareaElem.selectionStart + str.length

    this.props.onChange(updatedText)
  }

  render() {
    if (this.props.hidden) {
      return null
    }
    const { completion } = this.state
    return (
      <I18nContext.Consumer>
        {({ writingDirection }) => (
          <>
            {completion.open && (
              <CompletionMenu
                ariaLabel={window.static_translate('emoji')}
                items={completion.items}
                active={completion.active}
                onSelect={this.selectCompletion}
                onHover={i =>
                  this.setState(prev => ({
                    completion: { ...prev.completion, active: i },
                  }))
                }
              />
            )}
            <textarea
              className={
                'create-or-edit-message-input' +
                (browserSupportsCSSFieldSizing
                  ? ' use-field-sizing-css-prop'
                  : '')
              }
              style={{ '--maxLines': maxLines } as React.CSSProperties}
              id={
                this.props.isMessageEditingMode
                  ? 'composer-textarea-edit'
                  : 'composer-textarea-non-edit'
              }
              ref={this.textareaRef}
              rows={1}
              // intent={this.state.error ? 'danger' : 'none'}
              // large
              value={this.props.text}
              onKeyDown={this.onKeyDown}
              onKeyUp={this.onCaretMove}
              onClick={this.onCaretMove}
              onBlur={this.closeCompletion}
              onChange={this.onChange}
              onPaste={this.props.onPaste}
              placeholder={
                this.props.isMessageEditingMode
                  ? window.static_translate('edit_message')
                  : window.static_translate('write_message_desktop')
              }
              disabled={this.props.loadingDraft}
              dir={
                writingDirection === 'rtl'
                  ? 'rtl'
                  : 'auto' /* auto is based on content but defaults to ltr */
              }
              spellCheck={true}
              aria-keyshortcuts='Control+M'
            />
          </>
        )}
      </I18nContext.Consumer>
    )
  }
}
